import * as d3 from 'd3';
import type { PowerDataPoint } from '../utils';
import { getRowerColorByKey } from '../../../../../utils/rowerColors';
import { chartTooltipHtml as tt } from '../../common/chartTooltipClasses';

export interface TooltipConfig {
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  tooltipElement: HTMLDivElement | null;
  crosshair: d3.Selection<SVGLineElement, unknown, null, undefined>;
  xScale: d3.ScaleLinear<number, number>;
  plotWidth: number;
  plotHeight: number;
  chartData: PowerDataPoint[];
  renderRowerKeys: string[];
  isPlaying: boolean;
  perStrokeMode: boolean;
  margin: { top: number; right: number; bottom: number; left: number };
  actualWidth: number;
  actualHeight: number;
  zoomBehavior: d3.ZoomBehavior<SVGSVGElement, unknown>;
}

export function setupTooltip(config: TooltipConfig): {
  cleanup: () => void;
} {
  const {
    g,
    tooltipElement,
    crosshair,
    xScale,
    plotWidth,
    plotHeight,
    chartData,
    renderRowerKeys,
    isPlaying,
    perStrokeMode,
    margin,
    actualWidth,
    actualHeight,
    zoomBehavior,
  } = config;

  const tooltip = d3.select(tooltipElement);
  const hideTooltip = () => tooltip.style('opacity', 0);
  const bisect = d3.bisector((d: PowerDataPoint) => d.time).left;

  g.append('rect')
    .attr('class', 'interaction-layer')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', plotWidth)
    .attr('height', plotHeight)
    .attr('fill', 'transparent')
    .style('pointer-events', 'all')
    .call(zoomBehavior as any)
    .on('pointermove', (event) => {
      if (chartData.length === 0 || renderRowerKeys.length === 0) {
        hideTooltip();
        crosshair.style('opacity', 0);
        return;
      }
      const [px, py] = d3.pointer(event, g.node());
      if (px < 0 || px > plotWidth || py < 0 || py > plotHeight) {
        hideTooltip();
        crosshair.style('opacity', 0);
        return;
      }

      const shouldShowCrosshair = !(isPlaying && perStrokeMode);
      if (shouldShowCrosshair) {
        crosshair.attr('x1', px).attr('x2', px).style('opacity', 0.6);
      } else {
        crosshair.style('opacity', 0);
      }
      const domainTime = xScale.invert(px);
      let index = bisect(chartData, domainTime);
      index = Math.min(Math.max(index, 1), chartData.length - 1);
      const prev = chartData[index - 1] as PowerDataPoint;
      const next = chartData[index] as PowerDataPoint;
      const sample = domainTime - prev.time < next.time - domainTime ? prev : next;
      if (!sample) {
        hideTooltip();
        return;
      }
      const rows = renderRowerKeys
        .map((key) => {
          const value = (sample as PowerDataPoint)[key];
          if (value == null || !isFinite(value)) return null;
          return `<div class="${tt.row}">
                <span class="${tt.rowInner}">
                  <span class="inline-block h-2 w-2 rounded-full" style="background:${getRowerColorByKey(key)}"></span>
                  <span class="${tt.label}">${key.replace('Rower ', 'R')}</span>
                </span>
                <span class="${tt.value}">${Math.round(value)} W</span>
              </div>`;
        })
        .filter(Boolean)
        .join('');
      const tooltipHtml = `<div class="${tt.title}">Time: ${Math.round(
        sample.time
      )}s</div>${rows || `<div class="${tt.muted}">No visible rowers</div>`}`;

      tooltip.html(tooltipHtml);

      const tooltipNode = tooltip.node() as HTMLElement;
      if (!tooltipNode) return;

      const tooltipWidth = tooltipNode.offsetWidth || 200;
      const tooltipHeight = tooltipNode.offsetHeight || 100;

      const tooltipXOffset = 16;
      const tooltipYOffset = 16;

      let tooltipX = margin.left + px + tooltipXOffset;
      let tooltipY = margin.top + py - tooltipYOffset;

      if (tooltipX + tooltipWidth > actualWidth) {
        tooltipX = margin.left + px - tooltipWidth - tooltipXOffset;
      }

      if (tooltipX < 0) {
        tooltipX = 8;
      }

      if (tooltipY + tooltipHeight > actualHeight) {
        tooltipY = margin.top + py - tooltipHeight - tooltipYOffset;
      }

      if (tooltipY < 0) {
        tooltipY = 8;
      }

      tooltip.style('opacity', 1).style('left', `${tooltipX}px`).style('top', `${tooltipY}px`);
    })
    .on('pointerleave', () => {
      hideTooltip();
      crosshair.style('opacity', 0);
    });

  return {
    cleanup: () => {
      hideTooltip();
    },
  };
}
